import { useEffect, useMemo, useState } from 'react'

interface CountdownTimerProps {
  startedAt: string | Date
  slaMinutes: number
  onExpired?: () => void
  // Permite override en tests para evitar setInterval real.
  nowProvider?: () => number
}

interface CountdownDisplay {
  elapsedMin: number
  remainingMin: number
  pctConsumido: number
  level: 'green' | 'yellow' | 'red'
  label: string
}

function compute(now: number, startMs: number, slaMin: number): CountdownDisplay {
  const elapsedMs = Math.max(0, now - startMs)
  const elapsedMin = Math.floor(elapsedMs / 60_000)
  const remainingMin = slaMin - elapsedMin
  const pct = slaMin === 0 ? 1 : elapsedMin / slaMin

  let level: CountdownDisplay['level'] = 'green'
  if (pct >= 0.8) level = 'red'
  else if (pct >= 0.5) level = 'yellow'

  // Label: minutos restantes si >= 0, "vencido +Xmin" si < 0.
  const label = remainingMin >= 0
    ? `${remainingMin} min`
    : `vencido +${Math.abs(remainingMin)} min`

  return { elapsedMin, remainingMin, pctConsumido: pct, level, label }
}

const LEVEL_CLASSES: Record<CountdownDisplay['level'], string> = {
  green: 'bg-green-100 text-green-900 border-green-300',
  yellow: 'bg-amber-100 text-amber-900 border-amber-300',
  red: 'bg-red-100 text-red-900 border-red-300 animate-pulse',
}

export default function CountdownTimer({
  startedAt,
  slaMinutes,
  onExpired,
  nowProvider,
}: CountdownTimerProps) {
  const startMs = startedAt instanceof Date ? startedAt.getTime() : new Date(startedAt).getTime()
  // Estabilizamos getNow para que no genere una arrow nueva en cada render,
  // lo cual disparaba un loop infinito en useEffect (deps inestables).
  const getNow = useMemo(
    () => nowProvider ?? (() => Date.now()),
    [nowProvider],
  )
  const [state, setState] = useState<CountdownDisplay>(() =>
    compute(getNow(), startMs, slaMinutes),
  )

  useEffect(() => {
    setState(compute(getNow(), startMs, slaMinutes))
    const id = window.setInterval(() => {
      setState((prev) => {
        const next = compute(getNow(), startMs, slaMinutes)
        // Disparar onExpired solo al cruzar el limite (no en cada tick).
        if (prev.remainingMin >= 0 && next.remainingMin < 0) {
          onExpired?.()
        }
        return next
      })
    }, 30_000)
    return () => window.clearInterval(id)
  }, [startMs, slaMinutes, onExpired, getNow])

  return (
    <span
      role="timer"
      aria-live="polite"
      aria-label={`SLA: ${state.label}`}
      data-level={state.level}
      className={`inline-flex items-center gap-1 text-xs font-semibold border rounded px-2 py-0.5 ${LEVEL_CLASSES[state.level]}`}
    >
      <span aria-hidden="true">⏱</span>
      {state.label}
    </span>
  )
}
